<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="buttonClasses"
    @click="$emit('click', $event)"
  >
    <i v-if="loading" class="pi pi-spinner pi-spin mr-2" />
    <i v-else-if="icon" :class="[icon, { 'mr-2': !!$slots.default }]" />
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed } from "vue"

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger"
type ButtonSize = "sm" | "md" | "lg"

interface Props {
  variant?: ButtonVariant
  size?: ButtonSize
  type?: "button" | "submit" | "reset"
  disabled?: boolean
  loading?: boolean
  icon?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: "primary",
  size: "md",
  type: "button",
  disabled: false,
  loading: false,
  icon: undefined,
})

defineEmits<{
  click: [event: MouseEvent]
}>()

const baseClasses =
  "inline-flex items-center justify-center font-medium rounded-md transition-colors duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"

const variantClasses: Record<ButtonVariant, string> = {
  primary: "bg-green-500 text-white hover:bg-green-600 focus:ring-green-500",
  secondary:
    "bg-white text-navy-700 border border-navy-200 hover:bg-navy-50 focus:ring-navy-500",
  ghost: "bg-transparent text-navy-600 hover:bg-navy-50 focus:ring-navy-500",
  danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500",
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-4 py-2 text-sm",
  lg: "px-6 py-3 text-base",
}

const buttonClasses = computed(() => [
  baseClasses,
  variantClasses[props.variant],
  sizeClasses[props.size],
])
</script>
